import { kotLines, orderReady, pendingKitchen } from '@ims/core'
import type {
  DeliveryRow as CoreDelivery,
  DocLineInput,
  KotTicketRow as CoreKotTicket,
  OrderRequestItemRow as CoreRequestItem,
  OrderRequestRow as CoreRequest,
  RequestProduct,
  OrderItemRow as CoreOrderItem,
  OrderRow as CoreOrder,
} from '@ims/core'
import type {
  DeliveryRow,
  InvoiceOrderInfo,
  KotItemRow,
  KotTicketRow,
  OrderItemRow,
  OrderRequestItemRow,
  OrderRequestRow,
  OrderRow,
  Repository,
  TenantSettingsRow,
} from '@ims/data'
import type { CartLine } from './cart.ts'

// Restaurant orders in the app: rows as core's builders take them, and an
// order's lines as a quick-bill cart (checkout is quick bill with the cart filled in).

/** The row as clients write it (server-set columns dropped). */
export function orderRow(row: OrderRow): CoreOrder {
  const { created_by: _by, server_updated_at: _at, ...rest } = row
  return rest
}

export function orderItemRows(rows: readonly OrderItemRow[]): CoreOrderItem[] {
  return rows.map(({ created_by: _by, server_updated_at: _at, ...rest }) => rest)
}

export function deliveryRow(row: DeliveryRow): CoreDelivery {
  const { created_by: _by, server_updated_at: _at, ...rest } = row
  return rest
}

export function kotTicketRow(row: KotTicketRow): CoreKotTicket {
  const { created_by: _by, server_updated_at: _at, ...rest } = row
  return rest
}

/** The ticket each order item row went out on (the first, if two devices sent it). */
export function ticketByItem(
  tickets: readonly KotTicketRow[],
  sent: readonly KotItemRow[],
): Map<string, KotTicketRow> {
  const byId = new Map(tickets.map((t) => [t.id, t]))
  const out = new Map<string, KotTicketRow>()
  for (const link of sent) {
    const ticket = byId.get(link.kot_id)
    if (ticket && !out.has(link.order_item_id)) out.set(link.order_item_id, ticket)
  }
  return out
}

/** "12:10": the time of day, for tickets. */
export function clock(iso: string): string {
  return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
}

/** The start of today on this device, for the day's ticket numbers. */
export function startOfToday(): string {
  const d = new Date()
  d.setHours(0, 0, 0, 0)
  return d.toISOString()
}

/** The business's ready-made item notes ("Less spicy"), keeping only well-formed entries. */
export function quickNotes(
  settings: Pick<TenantSettingsRow, 'item_notes'> | null | undefined,
): string[] {
  const raw = settings?.item_notes
  if (!Array.isArray(raw)) return []
  return raw.filter((n): n is string => typeof n === 'string' && n.trim().length > 0)
}

/** Adds or removes a ready-made note in a comma-separated note. */
export function toggleNote(note: string, quick: string): string {
  const parts = note
    .split(',')
    .map((p) => p.trim())
    .filter(Boolean)
  const at = parts.findIndex((p) => p.toLowerCase() === quick.toLowerCase())
  if (at >= 0) parts.splice(at, 1)
  else parts.push(quick)
  return parts.join(', ')
}

export function requestRow(row: OrderRequestRow): CoreRequest {
  const { decided_by: _by, server_updated_at: _at, ...rest } = row
  return rest
}

export function requestItemRows(rows: readonly OrderRequestItemRow[]): CoreRequestItem[] {
  return rows.map(({ server_updated_at: _at, ...rest }) => rest)
}

/**
 * The requested products as they are right now (VAT, kitchen station), for
 * accepting a guest's order. A product that is gone or no longer sold is left out.
 */
export async function requestProducts(
  repo: Repository,
  productIds: readonly string[],
  defaultTaxRateId: string | null,
): Promise<Map<string, RequestProduct>> {
  const out = new Map<string, RequestProduct>()
  for (const id of new Set(productIds)) {
    const p = await repo.get('products', id)
    if (!p || p.deleted_at || !p.for_sale) continue
    const taxId = p.tax_rate_id ?? defaultTaxRateId
    const tax = taxId ? await repo.get('tax_rates', taxId) : null
    out.set(id, {
      priceIncludesTax: p.price_includes_tax,
      taxRateId: tax?.id ?? null,
      taxRateBp: tax?.rate_bp ?? 0,
      station: p.kitchen_station,
    })
  }
  return out
}

export const STATION_LABEL = { kitchen: 'Kitchen', bar: 'Bar' } as const

export const isOpenOrder = (order: Pick<OrderRow, 'status'>) =>
  order.status === 'open' || order.status === 'billed'

/**
 * An order's bill lines as cart lines. Prices are the order's (never re-priced);
 * the rest (unit, stock, batches, minimum price) comes from the product as it is now.
 */
export async function orderCartLines(
  repo: Repository,
  lines: readonly DocLineInput[],
): Promise<Omit<CartLine, 'key'>[]> {
  const out: Omit<CartLine, 'key'>[] = []
  for (const line of lines) {
    const product = line.productId ? await repo.get('products', line.productId) : null
    const unit = product?.unit_id ? await repo.get('units', product.unit_id) : null
    out.push({
      ...line,
      discountPaisa: 0,
      trackStock: product?.track_stock ?? false,
      unitSymbol: unit?.symbol ?? '',
      allowsDecimal: unit?.allows_decimal ?? false,
      mrpPaisa: product?.mrp_paisa ?? null,
      trackBatches: product?.track_batches ?? false,
      trackSerials: product?.track_serials ?? false,
      retailPricePaisa: product?.sell_price_paisa,
      wholesalePricePaisa: product?.wholesale_price_paisa ?? null,
      minPricePaisa: product?.min_price_paisa ?? null,
      fixedPrice: true,
    })
  }
  return out
}

/** "45m", "2h 10m": how long an order has been open. */
export function openFor(openedAt: string, now: Date): string {
  const minutes = Math.max(0, Math.floor((now.getTime() - new Date(openedAt).getTime()) / 60000))
  if (minutes < 60) return `${minutes}m`
  const hours = Math.floor(minutes / 60)
  return `${hours}h ${minutes % 60}m`
}

const SERVICE_LABEL = { takeaway: 'Takeaway', delivery: 'Delivery' } as const

/**
 * What staff call an order: its table's name for dine-in, else its number for
 * the day, its type and the name given ("#23 Takeaway · Sita").
 */
export function orderName(
  order: Pick<OrderRow, 'service_type' | 'order_no' | 'label'>,
  tableName: string | null,
): string {
  if (order.service_type === 'dine_in') return tableName ?? 'No table'
  const name = `#${order.order_no ?? '?'} ${SERVICE_LABEL[order.service_type]}`
  return order.label ? `${name} · ${order.label}` : name
}

/**
 * Where an order's back link goes: a dine-in order to the floor (the Tables tab), a takeaway
 * or delivery to its chip on the Orders tab.
 */
export function tablesPath(order: Pick<OrderRow, 'service_type'> | null | undefined): string {
  return !order || order.service_type === 'dine_in'
    ? '/tables'
    : `/orders?type=${order.service_type}`
}

/** The words on that back link. */
export function backLabel(order: Pick<OrderRow, 'service_type'> | null | undefined): string {
  return !order || order.service_type === 'dine_in' ? 'Back to tables' : 'Back to orders'
}

/** What a table is doing right now, worked out from its open orders (never stored). */
export type TableState = 'free' | 'busy' | 'ready' | 'pay' | 'call'

/**
 * A guest calling comes first (someone is waiting on us), then the bill being out,
 * then food waiting to be collected. `ready` is what the bell still shows on this device.
 */
export function tableState(
  orders: readonly Pick<OrderRow, 'status'>[],
  calling: boolean,
  ready: boolean,
): TableState {
  if (calling) return 'call'
  if (orders.length === 0) return 'free'
  if (orders.every((o) => o.status === 'billed')) return 'pay'
  return ready ? 'ready' : 'busy'
}

/**
 * Where an open order stands, for the Orders tab's list. Derived from the order and its
 * tickets, never stored.
 */
export function kitchenState(
  order: Pick<OrderRow, 'status'>,
  tickets: readonly Pick<KotTicketRow, 'status' | 'deleted_at'>[],
): { label: string; tone: 'neutral' | 'primary' | 'success' | 'warning' } {
  if (order.status === 'billed') return { label: 'Bill printed', tone: 'primary' }
  const live = tickets.filter((t) => !t.deleted_at)
  if (live.length === 0) return { label: 'Not sent', tone: 'neutral' }
  if (live.every((t) => t.status === 'ready')) return { label: 'Ready', tone: 'success' }
  if (live.some((t) => t.status === 'preparing')) return { label: 'Preparing', tone: 'warning' }
  return { label: 'In the kitchen', tone: 'neutral' }
}

/** How many lines of an order the kitchen has not been told about yet. */
export function unsentCount(detail: {
  items: readonly OrderItemRow[]
  sent: readonly KotItemRow[]
}): number {
  const pending = pendingKitchen(orderItemRows(detail.items), detail.sent)
  return [...pending.values()].reduce((sum, rows) => sum + kotLines(rows).length, 0)
}

/**
 * The Orders tab's list: orders waiting to pay (bill printed) first, then those the kitchen
 * has finished, then the rest, each group oldest first (the order the rows came in).
 */
export function sortOpenOrders<
  T extends {
    order: Pick<OrderRow, 'status'>
    tickets: readonly Pick<KotTicketRow, 'status' | 'deleted_at'>[]
  },
>(orders: readonly T[]): T[] {
  const rank = (o: T) => (o.order.status === 'billed' ? 0 : orderReady(o.tickets) ? 1 : 2)
  return orders
    .map((o, at) => ({ o, at }))
    .sort((a, b) => rank(a.o) - rank(b.o) || a.at - b.at)
    .map(({ o }) => o)
}

/**
 * What a restaurant bill prints under its number (canvas board BillTablePrint): the
 * table and guests, or the day's order number, who it is for, and who served it.
 */
export interface BillService {
  /** "Table T7 · Garden", "Takeaway #13", "Delivery #5". */
  heading: string
  /** "3 guests" beside a table's heading. */
  right: string | null
  /** "For: Hari" (takeaway) or "To: Jhamsikhel" (delivery). */
  extra: string | null
  servedBy: string | null
}

export function billService(
  order: Pick<OrderRow, 'service_type' | 'order_no' | 'label' | 'covers'>,
  where: { table: string | null; area: string | null; address: string | null },
  servedBy: string | null,
  customerName: string | null,
): BillService {
  if (order.service_type === 'dine_in')
    return {
      heading: where.table
        ? `Table ${where.table}${where.area ? ` · ${where.area}` : ''}`
        : 'Dine in',
      right: `${order.covers} ${order.covers === 1 ? 'guest' : 'guests'}`,
      extra: null,
      servedBy,
    }
  const heading = `${SERVICE_LABEL[order.service_type]} #${order.order_no ?? '?'}`
  if (order.service_type === 'delivery')
    return { heading, right: null, extra: where.address ? `To: ${where.address}` : null, servedBy }
  // The customer's name is printed already; the pickup name only when it says more.
  const label = order.label?.trim() || null
  return {
    heading,
    right: null,
    extra: label && label !== customerName ? `For: ${label}` : null,
    servedBy,
  }
}

/** The bill's order, if a restaurant order made it (checkout closes the order with it). */
export async function orderServiceFor(
  repo: Repository,
  invoice: { id: string; customer_name: string | null },
  serverName: (id: string | null) => string | null,
): Promise<BillService | null> {
  const order = (await repo.list('orders', { where: { invoice_id: invoice.id }, limit: 1 })).rows[0]
  if (!order) return null
  const table = order.table_id ? await repo.get('dining_tables', order.table_id) : null
  const area = table?.area_id ? await repo.get('dining_areas', table.area_id) : null
  const delivery =
    order.service_type === 'delivery' ? await repo.deliveries.forOrder(order.id) : null
  return billService(
    order,
    { table: table?.name ?? null, area: area?.name ?? null, address: delivery?.address ?? null },
    serverName(order.server_id ?? order.created_by ?? null),
    invoice.customer_name,
  )
}

/**
 * A restaurant bill's line in the invoices list: "Table T7 · 3 guests · Sita Rai",
 * "Takeaway #13 · Hari · Ram Thapa" (canvas board InvoicesTables).
 */
export function invoiceOrderLine(
  info: Pick<InvoiceOrderInfo, 'serviceType' | 'tableName' | 'covers' | 'orderNo' | 'label'>,
  customerName: string | null,
  server: string | null,
): string {
  const head =
    info.serviceType === 'dine_in'
      ? [
          `Table ${info.tableName ?? '?'}`,
          `${info.covers} ${info.covers === 1 ? 'guest' : 'guests'}`,
        ]
      : [`${SERVICE_LABEL[info.serviceType]} #${info.orderNo ?? '?'}`, customerName ?? info.label]
  return [...head, server].filter(Boolean).join(' · ')
}
